import Dexie, { type Table } from 'dexie';
import { BrandBusiness } from '@models/brandBusiness.interface';
import { Brand } from '@models/brand.interface';
import { CategoryBusiness } from '@models/categoryBusiness.interface';
import { Category } from '@models/category.interface';
import { ICashMovement } from '@models/cash.interface';
import { ICashSession } from '@models/cashSession.interface';
import { InventoryMovement } from '@models/inventoryMovment';
import { ProductBusiness } from '@models/productBusiness';
import { Product } from '@models/product.interface';
import { Sale } from '@models/sale.interface';
import { Supplier } from '@models/supplier.interface';

export interface CatalogMetadata {
  key: string;
  value: string;
}

export type SyncOperation = 'create' | 'update' | 'delete';

export interface SyncMetadata {
  synchronized: boolean;
  updatedAt: string;
  clientOperationId?: string | null;
}

export interface SyncQueueItem {
  id?: number;
  entidad: string;
  operacion: SyncOperation;
  payload: Record<string, unknown>;
  intentos: number;
  fecha: string;
}

export type OfflineProduct = Product & SyncMetadata;
export type OfflineCategory = Category & SyncMetadata;
export type OfflineBrand = Brand & SyncMetadata;
export type OfflineSupplier = Supplier & SyncMetadata;
export type OfflineSale = Sale & SyncMetadata;
export type OfflineCashSession = ICashSession & SyncMetadata;
export type OfflineCashMovement = ICashMovement & SyncMetadata;
export type OfflineInventoryMovement = InventoryMovement & SyncMetadata;
export type OfflineProductBusiness = ProductBusiness & SyncMetadata;
export type OfflineCategoryBusiness = CategoryBusiness & SyncMetadata;
export type OfflineBrandBusiness = BrandBusiness & SyncMetadata;

export class AppDb extends Dexie {
  products!: Table<any, number>;
  categories!: Table<any, number>;
  brands!: Table<any, number>;
  suppliers!: Table<any, number>;
  sales!: Table<any, number>;
  cashSessions!: Table<any, number>;
  cashMovements!: Table<any, number>;
  inventoryMovements!: Table<any, number>;
  productBusinesses!: Table<any, number>;
  categoryBusinesses!: Table<any, number>;
  brandBusinesses!: Table<any, number>;
  syncQueue!: Table<SyncQueueItem, number>;
  catalog_metadata!: Table<CatalogMetadata, string>;

  constructor() {
    super('posSellOfflineDb');

    this.version(1).stores({
      products: 'id, synchronized, updatedAt, supplier_id, status_id',
      categories: 'id, synchronized, updatedAt, parent_id, order, is_custom',
      brands: 'id, synchronized, updatedAt, is_custom',
      suppliers: 'id, synchronized, updatedAt, is_active',
      sales: 'id, synchronized, updatedAt, business_id, user_id, cash_session_id',
      cashSessions: 'id, synchronized, updatedAt, business_id, status, opened_by_user_id',
      cashMovements: 'id, synchronized, updatedAt, business_id, cash_session_id, user_id, type',
      inventoryMovements: 'id, synchronized, updatedAt, product_inventory_id, product_business_id, direction',
      // OFFLINE: name_lower is a denormalized field for O(log n) prefix search offline.
      productBusinesses: 'id, synchronized, updatedAt, product_id, business_id, sku, barcode, is_enabled, is_complete, name_lower',
      categoryBusinesses: 'id, synchronized, updatedAt, category_id, business_id, is_enabled',
      brandBusinesses: 'id, synchronized, updatedAt, brand_id, business_id, is_enabled',
      syncQueue: '++id, entidad, operacion, intentos, fecha',
      catalog_metadata: 'key',
    });
  }
}

export const appDb = new AppDb();
